// Utilities for date and time formatting per event calendar settings.

const DAYS_ES_LONG = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MONTHS_ES_LONG = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// Parse a YYYY-MM-DD string as a *local* date (no timezone shift).
export function parseYMD(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Convert local date to YYYY-MM-DD
export function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatLongDate(ymd) {
  const d = parseYMD(ymd);
  const dow = DAYS_ES_LONG[d.getDay()];
  const mon = MONTHS_ES_LONG[d.getMonth()];
  return `${dow} ${d.getDate()} de ${mon} ${d.getFullYear()}`;
}

export function formatShortDate(ymd, format = "dd/mm/yyyy") {
  const d = parseYMD(ymd);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return format === "mm/dd/yyyy" ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
}

export function formatTime(hhmm, format = "12h") {
  const [h, m] = hhmm.split(":").map(Number);
  if (format === "24h") return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const suffix = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

// Add minutes to a HH:MM string (24h) → new HH:MM
export function addMinutes(hhmm, minutes) {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

// Generate slots between start and end (24h HH:MM) with a given duration in minutes.
export function generateTimeSlots(start, end, durationMin) {
  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };
  const slots = [];
  const endMin = toMin(end);
  let cur = toMin(start);
  while (cur + durationMin <= endMin) {
    const h = Math.floor(cur / 60);
    const m = cur % 60;
    slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    cur += durationMin;
  }
  return slots;
}

// Recurrence generation.
// weekly: pick days-of-week (0=Sun..6=Sat), between start and end
export function generateWeekly(startYMD, endYMD, dow /* array of numbers */) {
  const out = [];
  const start = parseYMD(startYMD);
  const end = parseYMD(endYMD);
  const cur = new Date(start);
  const dowSet = new Set(dow);
  while (cur <= end) {
    if (dowSet.has(cur.getDay())) out.push(toYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// monthly: nth weekday of the month. nth: 1..4 or 5 (=last)
export function generateMonthlyNth(startYMD, endYMD, nth, dow) {
  const out = [];
  const start = parseYMD(startYMD);
  const end = parseYMD(endYMD);
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur <= end) {
    const monthDates = [];
    const probe = new Date(cur);
    while (probe.getMonth() === cur.getMonth()) {
      if (probe.getDay() === dow) monthDates.push(new Date(probe));
      probe.setDate(probe.getDate() + 1);
    }
    let picked = null;
    if (nth === 5) picked = monthDates[monthDates.length - 1];
    else picked = monthDates[nth - 1];
    if (picked && picked >= start && picked <= end) out.push(toYMD(picked));
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

export const DOW_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
export const DOW_LABELS_LONG = DAYS_ES_LONG;
